import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Camera, LogOut, Plus, Link2, Copy, DollarSign, Images, Trash2, Settings, ExternalLink, LayoutGrid, Sparkles, ArrowLeft, Users } from 'lucide-react';
import NotificationBell from '../components/NotificationBell';
import { useApp } from '../context/AppContext';
import { useToast } from '../hooks/use-toast';
import { albumAPI } from '../lib/api';
export default function UserPanel() {
  const { currentUser, albums, logoutUser, createAlbum, deleteAlbum, updateSelf } = useApp();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [showNew, setShowNew] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  if (!currentUser) {
    navigate('/auth');
    return null;
  }

  const myAlbums = albums;
  const totalRevenue = myAlbums.reduce((s, a) => s + (a.revenue || 0), 0);
  const totalSales = myAlbums.reduce((s, a) => s + (a.sales || 0), 0);

  const copyLink = (code) => {
    const url = `${window.location.origin}/album/${code}`;
    navigator.clipboard.writeText(url);
    toast({ title: 'Link copiado', description: url });
  };
  const releasePendingDownload = async (album) => {
  try {
    const data = await albumAPI.listPurchases(album.code);
    const pending = (data.purchases || []).filter(
      (p) => p.status !== "released"
    );

    if (pending.length === 0) {
      alert("Não há download aguardando liberação.");
      return;
    }

    if (pending.length > 1) {
      alert("Há mais de uma compra aguardando. Abra a galeria para escolher.");
      return;
    }

    await albumAPI.releasePurchase(album.code, pending[0].id);
    alert("Download liberado!");
  } catch (err) {
    alert(err.message || "Erro ao liberar");
  }
};
  return (
    <div className="grain min-h-screen">
      {/* Top bar */}
      <header className="border-b border-[#1c1710]">
        <div className="max-w-6xl mx-auto px-6 md:px-8 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="text-[#a89e87] hover:text-[#e6dcc7] inline-flex items-center gap-1.5 text-sm" title="Voltar ao início">
              <ArrowLeft size={16} /> Início
            </Link>
            <div className="h-5 w-px bg-[#2a2318]" />
            <Link to="/" className="flex items-center gap-2.5">
              <span className="brand-mark"><Camera size={16} strokeWidth={2.2} /></span>
              <span className="font-serif text-[20px] text-[#f2ecdf]">AXSELL</span>
            </Link>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <NotificationBell />
            <div className="relative">
  <button
    onClick={() => setShowMenu(!showMenu)}
    className="btn-outline-dark px-3 py-2 rounded-md text-xl"
  >
    menu
  </button>

  {showMenu && (
    <div className="absolute right-0 mt-2 w-44 bg-white border rounded-lg shadow-lg z-50 overflow-hidden">
      <Link
        to="/painel/clientes"
        onClick={() => setShowMenu(false)}
        className="block px-4 py-3 hover:bg-gray-100"
      >
        Clientes
      </Link>

      <Link
        to="/galeria"
        onClick={() => setShowMenu(false)}
        className="block px-4 py-3 hover:bg-gray-100"
      >
        Galeria
      </Link>

      <button
        onClick={() => {
          setShowSettings(true);
          setShowMenu(false);
        }}
        className="block w-full text-left px-4 py-3 hover:bg-gray-100"
      >
        Ajustes
      </button>

      <button
        onClick={async () => {
          setShowMenu(false);
          await logoutUser();
          navigate("/");
        }}
        className="block w-full text-left px-4 py-3 hover:bg-gray-100"
      >
        Sair
      </button>
    </div>
  )}
</div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 md:px-8 py-10">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="text-xs uppercase tracking-[0.14em] text-[#a89e87]">Painel</div>
            <h1 className="font-serif text-4xl md:text-5xl text-[#f2ecdf] mt-1">Olá, {currentUser.name}</h1>
            <p className="text-sm text-[#a89e87] mt-1">Gerencie seus álbuns e acompanhe as vendas ⚠️ **Aviso importante:** As fotos são **excluídas automaticamente da galeria após 7 dias.</p>
          </div>
          <button onClick={() => setShowNew(true)} className="btn-gold px-4 py-2.5 rounded-md text-sm font-medium inline-flex items-center gap-2">
            <Plus size={16} /> Novo álbum
          </button>
        </div>

        <div className="grid sm:grid-cols-3 gap-4 mb-10">
          <Stat label="Álbuns publicados" value={myAlbums.length} icon={Images} />
          <Stat label="Fotos vendidas" value={totalSales} icon={DollarSign} />
          <Stat label="Receita total" value={`R$ ${totalRevenue.toFixed(2)}`} icon={DollarSign} />
        </div>

        <h2 className="font-serif text-2xl text-[#f2ecdf] mb-4">Seus álbuns</h2>
        <div className="space-y-3">
          {myAlbums.length === 0 && (
            <div className="card-glow rounded-xl p-8 text-center text-[#a89e87]">
              Nenhum álbum ainda. Crie seu primeiro ensaio.
            </div>
          )}
          {myAlbums.map(a => (
            <div key={a.code} className="card-glow rounded-xl p-5 flex items-center gap-4 flex-wrap">
              <div className="w-16 h-16 rounded-md overflow-hidden bg-[#1a140c] flex-shrink-0">
                {a.coverUrls?.[0] && <img src={a.coverUrls[0]} alt="" className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1 min-w-[200px]">
                <div className="font-serif text-lg text-[#f2ecdf]">{a.title}</div>
                <div className="text-xs text-[#a89e87] mt-0.5">{a.client} • {a.photoCount ?? (a.photos?.length || 0)} fotos • R$ {a.pricePerPhoto}/foto</div>
                <div className="text-xs mt-1">
                  <span className="text-[#7d735e]">Link: </span>
                  <span className="link-code">/album/{a.code}</span>
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs text-[#a89e87]">Vendas</div>
                <div className="gold-text font-medium">R$ {(a.revenue || 0).toFixed(2)}</div>
              </div>
              <div className="flex items-center gap-2">
                <Link to={`/galeria/${a.code}`} className="btn-outline-dark p-2 rounded-md" title="Ver galeria">
                  <LayoutGrid size={15} />
                </Link>
                <button onClick={() => copyLink(a.code)} className="btn-outline-dark p-2 rounded-md" title="Copiar link">
                  <Copy size={15} />
                </button>
                <Link to={`/album/${a.code}`} className="btn-outline-dark p-2 rounded-md" title="Ver como cliente">
                  <ExternalLink size={15} />
                </Link>
                
                <button onClick={async () => { if (window.confirm('Excluir este álbum?')) await deleteAlbum(a.code); }} className="btn-outline-dark p-2 rounded-md text-red-400" title="Excluir">
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </main>

      {showNew && <NewAlbumModal onClose={() => setShowNew(false)} onCreate={async (data) => {
        try {
          await createAlbum(data);
          toast({ title: 'Álbum criado', description: data.title });
          setShowNew(false);
        } catch (e) { toast({ title: 'Erro ao criar álbum' }); }
      }} />}

      {showSettings && <SettingsModal user={currentUser} onClose={() => setShowSettings(false)} onSave={async (patch) => {
        await updateSelf(patch);
        toast({ title: 'Ajustes salvos' });
        setShowSettings(false);
      }} />}
    </div>
  );
}

function Stat({ label, value, icon: Icon }) {
  return (
    <div className="card-glow rounded-xl p-5">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-md bg-[#1a140c] flex items-center justify-center feature-icon">
          <Icon size={18} />
        </div>
        <div>
          <div className="text-xs text-[#a89e87]">{label}</div>
          <div className="font-serif text-2xl text-[#f2ecdf]">{value}</div>
        </div>
      </div>
    </div>
  );
}

function NewAlbumModal({ onClose, onCreate }) {
  const [f, setF] = useState({ title: '', client: '', date: new Date().toISOString().slice(0,10), pricePerPhoto: 25, packagePrice: 180, packageCount: 10, extraPhotoPrice: 20, albumPrice: 320 });
  const [photoUrls, setPhotoUrls] = useState('');
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="card-glow rounded-2xl p-6 md:p-8 max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <h3 className="font-serif text-2xl text-[#f2ecdf] mb-4">Novo álbum</h3>
        <div className="space-y-3 text-sm">
          <Input label="Título" value={f.title} onChange={v => setF({...f, title: v})} />
          <Input label="Cliente" value={f.client} onChange={v => setF({...f, client: v})} />
          <Input label="Data" type="date" value={f.date} onChange={v => setF({...f, date: v})} />
          <div className="grid grid-cols-2 gap-3">
            <Input label="Preço por foto (R$)" type="number" value={f.pricePerPhoto} onChange={v => setF({...f, pricePerPhoto: Number(v)})} />
            <Input label="Preço do álbum (R$)" type="number" value={f.albumPrice} onChange={v => setF({...f, albumPrice: Number(v)})} />
            <Input label="Fotos no pacote" type="number" value={f.packageCount} onChange={v => setF({...f, packageCount: Number(v)})} />
            <Input label="Preço do pacote (R$)" type="number" value={f.packagePrice} onChange={v => setF({...f, packagePrice: Number(v)})} />
            <div className="col-span-2">
              <Input label="R$ por foto extra (após completar o pacote)" type="number" value={f.extraPhotoPrice} onChange={v => setF({...f, extraPhotoPrice: Number(v)})} />
              <p className="text-[11px] text-[#7d735e] mt-1">Quanto será cobrado por cada foto adicional além das {f.packageCount || 10} do pacote.</p>
            </div>
          </div>
          <label className="block">
            <span className="block text-xs uppercase tracking-wider text-[#a89e87] mb-1.5">URLs das fotos (uma por linha)</span>
            <textarea rows={4} value={photoUrls} onChange={e => setPhotoUrls(e.target.value)}
              placeholder="https://... \nhttps://..."
              className="w-full bg-[#12100b] border border-[#2a2318] focus:border-[#d6af6c] focus:outline-none rounded-md px-3 py-2 text-sm text-[#f2ecdf] placeholder:text-[#5c5343]" />
          </label>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="btn-outline-dark px-4 py-2 rounded-md text-sm">Cancelar</button>
          <button onClick={() => {
            const photos = photoUrls.split('\n').map(s => s.trim()).filter(Boolean).map((url, i) => ({ id: `n${Date.now()}${i}`, url, name: `IMG_${i+1}.jpg` }));
            onCreate({ ...f, photos });
          }} className="btn-gold px-4 py-2 rounded-md text-sm font-medium">Criar</button>
        </div>
      </div>
    </div>
  );
}

function SettingsModal({ user, onClose, onSave }) {
  const [pixKey, setPixKey] = useState(user.pixKey || '');
  const [name, setName] = useState(user.name);
  const [logo, setLogo] = useState(user.logo || '');
  // CONFIGURAÇÕES DA MARCA D'ÁGUA

const [watermarkSize, setWatermarkSize] = useState(
  user.watermarkSize ?? 25
);

const [watermarkOpacity, setWatermarkOpacity] = useState(
  user.watermarkOpacity ?? 50
);

const [watermarkPosition, setWatermarkPosition] = useState(
  user.watermarkPosition || 'center'
);
  const logoInputRef = React.useRef();

  const onLogoFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const { compressImageFile } = await import('../lib/image');
      const compressed = await compressImageFile(file, 500, 0.85);
      setLogo(compressed.dataUrl);
    } catch (err) {
      console.error('logo compress fail', err);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="card-glow rounded-2xl p-6 md:p-8 max-w-md w-full max-h-[92vh] overflow-y-auto">
        <h3 className="font-serif text-2xl text-[#f2ecdf] mb-4">Ajustes</h3>
        <div className="space-y-4">
          <Input label="Nome do estúdio" value={name} onChange={setName} />
          <Input label="Chave Pix (email, CPF, telefone ou aleatória)" value={pixKey} onChange={setPixKey} />
          <p className="text-xs text-[#7d735e] -mt-2">Sua chave Pix será usada para gerar o QR Code na finalização da compra.</p>

          <div>
            <span className="block text-xs uppercase tracking-wider text-[#a89e87] mb-1.5">Logo do estúdio (marca d'água)</span>
            <div className="flex items-center gap-3">
              <div className="w-20 h-20 rounded-lg bg-[#12100b] border border-[#2a2318] overflow-hidden flex items-center justify-center flex-shrink-0">
                {logo ? (
                  <img src={logo} alt="Logo" className="w-full h-full object-contain p-2" />
                ) : (
                  <span className="text-[10px] text-[#5c5343] text-center px-1">Sem logo</span>
                )}
              </div>
              <div className="flex-1 space-y-2">
                <input ref={logoInputRef} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={onLogoFile} />
                <button type="button" onClick={() => logoInputRef.current?.click()} className="btn-outline-dark w-full py-2 rounded-md text-xs font-medium">
                  {logo ? 'Trocar logo' : 'Enviar logo'}
                </button>
                {logo && (
                  <button type="button" onClick={() => setLogo('')} className="w-full py-1 rounded-md text-[11px] text-red-400 hover:text-red-300">
                    Remover
                  </button>
                )}
              </div>
            </div>
            <p className="text-xs text-[#7d735e] mt-2">PNG com fundo transparente funciona melhor. Será aplicado como marca d'água em todas as fotos e vídeos.</p>
          </div>
          {/* CONFIGURAÇÕES DA MARCA D'ÁGUA */}

<div className="mt-6 space-y-5">

  <h3 className="text-lg font-semibold text-[#f2ecdf]">
    Ajustes da Marca d'Água
  </h3>

  {/* TAMANHO */}
  <div>
    <label className="block mb-2 text-sm">
      Tamanho da marca d'água: {watermarkSize}%
    </label>

    <input
      type="range"
      min="5"
      max="100"
      value={watermarkSize}
      onChange={(e) =>
        setWatermarkSize(Number(e.target.value))
      }
      className="w-full cursor-pointer"
    />
  </div>

  {/* TRANSPARÊNCIA */}
  <div>
    <label className="block mb-2 text-sm">
      Visibilidade: {watermarkOpacity}%
    </label>

    <input
      type="range"
      min="0"
      max="100"
      value={watermarkOpacity}
      onChange={(e) =>
        setWatermarkOpacity(Number(e.target.value))
      }
      className="w-full cursor-pointer"
    />
  </div>

  {/* POSIÇÃO */}
  <div>
    <label className="block mb-2 text-sm">
      Posição da marca d'água
    </label>

    <select
      value={watermarkPosition}
      onChange={(e) =>
        setWatermarkPosition(e.target.value)
      }
      className="w-full bg-[#12100e] border border-[#2a2318] rounded-lg p-3 text-white"
    >
      <option value="center">Centro</option>
      <option value="top-left">Superior esquerdo</option>
      <option value="top-right">Superior direito</option>
      <option value="bottom-left">Inferior esquerdo</option>
      <option value="bottom-right">Inferior direito</option>
    </select>
  </div>

  {/* PRÉ-VISUALIZAÇÃO */}
  <div>
    <p className="mb-2 text-sm font-medium">
      Pré-visualização da marca d'água
    </p>

    <div className="relative w-full h-56 bg-gray-300 rounded-xl overflow-hidden">

      {logo && (
        <img
          src={logo}
          alt="Prévia da marca d'água"
          className="absolute object-contain"
          style={{
            width: `${watermarkSize}%`,
            maxHeight: "90%",
            opacity: watermarkOpacity / 100,

            top: watermarkPosition.startsWith("top")
              ? "5%"
              : watermarkPosition === "center"
              ? "50%"
              : "auto",

            bottom: watermarkPosition.startsWith("bottom")
              ? "5%"
              : "auto",

            left: watermarkPosition.endsWith("left")
              ? "5%"
              : watermarkPosition === "center"
              ? "50%"
              : "auto",

            right: watermarkPosition.endsWith("right")
              ? "5%"
              : "auto",

            transform: watermarkPosition === "center"
              ? "translate(-50%, -50%)"
              : "none"
          }}
        />
      )}

      {!logo && (
        <div className="flex items-center justify-center h-full text-gray-600 text-sm">
          Envie um logotipo para visualizar
        </div>
      )}

    </div>
  </div>

</div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="btn-outline-dark px-4 py-2 rounded-md text-sm">Cancelar</button>
          <button onClick={() => onSave({
  name,
  pixKey,
  logo,
  watermarkSize,
  watermarkOpacity,
  watermarkPosition
})}  className="btn-gold px-4 py-2 rounded-md text-sm font-medium">Salvar</button>
        </div>
      </div>
    </div>
  );
}

function Input({ label, type = 'text', value, onChange }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-wider text-[#a89e87] mb-1.5">{label}</span>
      <input type={type} value={value} onChange={e => onChange(e.target.value)}
        className="w-full bg-[#12100b] border border-[#2a2318] focus:border-[#d6af6c] focus:outline-none rounded-md px-3 py-2 text-sm text-[#f2ecdf]" />
    </label>
  );
}
